import React, { useEffect, useState } from "react";
import TableComponent from "../../common/TableComponent/TableComponent";
import IconButton from "../../common/IconButton/IconButton";
import PlusIcon from "../../../assets/PlusIcon";
import ModalComponent from "../../common/ModalComponent/ModalComponent";
import { getAllAdminList, createNewAdmin, updateAdmin, revokeAdmin } from "../../../services/foundation/administrator-service";
import { toast } from "react-toastify";
import "./AdministratorManagement.css";
import Loader from "../../common/Loader/Loader";
import { DataTableComponent } from "../../common/TableComponent/DataTableComponent";
import { formatDate } from "../../../utils/dateFormat";
import { useTranslation, Trans } from "react-i18next";
import validateFields from "../../../utils/ValidationInput";

const AdminManagement = () => {
  const { t } = useTranslation();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState("add");
  const [formData, setFormData] = useState({
    email: "",
    firstName: "",
    lastName: "",
    comments: "",
  });
  const [editingAdmin, setEditingAdmin] = useState(null);
  const isEditMode = modalType === "edit";
  const isAddMode = modalType === "add";
  const isRevokeMode = modalType === "revoke";


  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const res = await getAllAdminList();
      const adminsArray = Array.isArray(res) ? res : res.adminList || res.data || [];
      setAdmins(adminsArray);
    } catch (error) {
      toast.error(t("fetchAdminsFailedError"), { position: "top-center" });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (type, data = null) => {
    setModalType(type);
    if (data) {
      setFormData({
        email: data.email || "",
        firstName: data.firstName || "",
        lastName: data.lastName || "",
        comments: data.comments || "",
      });
      setEditingAdmin(data);
    } else {
      setFormData({ email: "", firstName: "", lastName: "", comments: "" });
      setEditingAdmin(null);
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setFormData({ email: "", firstName: "", lastName: "", comments: "" });
    setEditingAdmin(null);
  };

  const handleInputChange = (target) => {
    const { name, value } = target;
    toast.dismiss();

    if (name === "firstName" && value.length > 30) {
      toast.error(t("firstNameCannotExceed", { number: 30 }), { position: "top-center" });
      return;
    }
    if (name === "lastName" && value.length > 30) {
      toast.error(t("lastNameCannotExceed", { number: 30 }), { position: "top-center" });
      return;
    }
    if (name === "comments" && value.length > 255) {
      toast.error(t("commentsCannotExceed", { number: 255 }), { position: "top-center" });
      return;
    }
    setFormData((prev) => ({
      ...prev,
      [target.name]: target.value,
    }));
  };

  const trimText = (text) => {
    return text.trim().replace(/\s+/g, " ");
  };

  const handleAddAdmin = async () => {
    setLoading(true);
    try {
      // Required fields with types (text or email)
      const requiredFields = [
        { label: t("email"), key: "Email", type: "email", value: trimText(formData.email) },
        { label: t("firstName"), key: "First Name", type: "text", value: trimText(formData.firstName) },
        { label: t("lastName"), key: "Last Name", type: "text", value: trimText(formData.lastName) },
      ];
      const isValid = validateFields(formData, requiredFields, 'byValue');
      if (!isValid) {
          setLoading(false);
          return;
      }

      const payload = {
        ...formData,
        userName: formData.firstName + formData.lastName,
      };
      const response = await createNewAdmin(payload);
      if (response) {
        fetchAdmins();
        handleCloseModal();
      }
    } catch (error) {
      console.error("Failed to add admin:", error);
      toast.error(t("addAdminFailedError"), { position: "top-center" });
    } finally {
      setLoading(false);
    }
  };

  const handleEditAdmin = async () => {
    setLoading(true);

    // Required fields with types (text or email)
    const requiredFields = [
      { label: t("email"), key: "Email", type: "email", value: trimText(formData.email) },
      { label: t("firstName"), key: "First Name", type: "text", value: trimText(formData.firstName) },
      { label: t("lastName"), key: "Last Name", type: "text", value: trimText(formData.lastName) },
    ];
    const isValid = validateFields(formData, requiredFields, 'byValue');
    if (!isValid) {
        setLoading(false);
        return;
    } else {
      if (!editingAdmin?.userId) {
        toast.error(t("invalidAdminUpdateError"), { position: "top-center" });
        setLoading(false);
        return;
      }
    }

    try {
      const payload = {
        email: formData.email, // Ensure email is explicitly set
        firstName: formData.firstName,
        lastName: formData.lastName,
        comments: formData.comments,
        username: formData.firstName + formData.lastName, // Generate username from first and last name
      };

      const response = await updateAdmin(payload, editingAdmin.userId);
      if (response) {
        fetchAdmins();
        handleCloseModal();
      }
    } catch (error) {
      toast.error(t("updateAdminFailedError"), { position: "top-center" });
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeAdmin = async () => {
    setLoading(true);
    if (!editingAdmin?.userId) {
      toast.error(t("invalidAdminRevokeError"), { position: "top-center" });
      setLoading(false);
      return;
    }
    try {
      const response = await revokeAdmin(editingAdmin?.userId, editingAdmin?.email);
      if (response) {
        fetchAdmins();
        handleCloseModal();
      }
    } catch (error) {
      console.error("Failed to revoke admin:", error);
      toast.error(t("revokeAdminFailedError"), { position: "top-center" });
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { name: t("emailId"), field: "email", selector: row => row.email, sortable: true, width: "200px" },
    { name: t("firstName"), field: "firstName", selector: row => row.firstName, sortable: true },
    { name: t("lastName"), field: "lastName", selector: row => row.lastName, sortable: true },
    { name: t("addedDate"), field: "addedDate", selector: row => row.addedDate, cell: row => formatDate(row.addedDate), sortable: true },
    { name: t("addedBy"), field: "addedBy", selector: row => row.addedBy, sortable: true },
    { name: t("comments"), field: "comments", selector: row => row.comments, sortable: true },
  ];

  const fieldsData = [
    { fieldName: t("emailId"), name: "email", type: "email", disabled: (modalType === "revoke" || modalType === "edit"), required: true },
    { fieldName: t("firstName"), name: "firstName", type: "input", required: true },
    { fieldName: t("lastName"), name: "lastName", type: "input", required: true },
    { fieldName: t("comments"), name: "comments", type: "textarea" },
  ];

  return (
    <div className="admin-manag-Container">
      {loading ? (
        <Loader /> // Show loader when loading is true
      ) : (
        <>
          {/* <div className="admin-mang-header">
            <IconButton
              text="Add Administrator"
              onClick={() => handleOpenModal("add")}
              style={{
                backgroundColor: "#F0F0F0",
                color: "#454647",
                border: "1px solid #B3B2B4",
              }}
              icon={<PlusIcon />}
            />
          </div> */}
          <div className="surveyHeader">
            <h2 className="surveyTitle">{t("listofAdministrators")}</h2>
            <IconButton
              text={t("addAdministrator")}
              onClick={() => handleOpenModal("add")}
              style={{
                backgroundColor: "#F0F0F0",
                color: "#454647",
                border: "1px solid #B3B2B4",
              }}
              icon={<PlusIcon />}
            />
          </div>
          <hr></hr>

          <div style={{ marginTop: "1rem" }}>
            <DataTableComponent
              columns={columns}
              data={admins}
              tableName="AdministratorManagement"
              onEditDetails={(row) => handleOpenModal("edit", row)}
              onRevoke={(row) => handleOpenModal("revoke", row)}
              pageCount={5}
            />
          </div>

          <ModalComponent
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            modalTitle={
              modalType === "add"
                ? t("addAdministrator")
                : modalType === "edit"
                  ? t("updateAdministrator")
                  : t("revokeAdministrator")
            }
            modalText={
              modalType === "revoke" && (
                <p>
                  <Trans i18nKey="revokeConfirm" values={{ email: formData?.email }} components={{ strong: <strong /> }} />
                </p>
              )
            }
            fieldsData={modalType === "revoke" ? [] : fieldsData}
            formData={formData}
            onChangeInput={handleInputChange}
            buttons={[
              (isAddMode || isEditMode) && (
                <button
                  key="addOrEdit"
                  className="saveButton"
                  onClick={isEditMode ? handleEditAdmin : handleAddAdmin}
                >
                  {isEditMode ? t("update") : t("add")}
                </button>
              ),
              isRevokeMode && (
                <button key="revoke" className="saveButton" onClick={handleRevokeAdmin}>
                  {t("revoke")}
                </button>
              ),
              <button key="cancel" className="cancelButton" onClick={handleCloseModal}>
                {t("cancel")}
              </button>,
            ].filter(Boolean)}
          />
        </>
      )}
    </div>
  );
};

export default AdminManagement;